/**
 * Connection Request API — Supabase
 */

import { supabase, TABLES, getDocument, fromDb, fromDbList } from './db';
import { ConnectionRequest } from '../types';
import {
  notifyConnectionAccepted,
  notifyConnectionRejected,
  notifyOwnerConnectionAccepted,
} from './notifications';

// ============================================================================
// CREATE
// ============================================================================

export async function sendConnectionRequest(
  taskId: string,
  taskerId: string,
  taskOwnerId: string,
  message?: string
): Promise<string> {
  // Check for existing request (unique constraint handles duplicates)
  const { data: existing } = await supabase
    .from(TABLES.CONNECTION_REQUESTS)
    .select('id, status')
    .eq('task_id', taskId)
    .eq('tasker_id', taskerId)
    .maybeSingle();

  if (existing) {
    if (existing.status === 'pending') throw new Error('Connection request already exists');
    if (existing.status === 'accepted') throw new Error('Already connected to this task');
    // If rejected, allow re-sending by updating status back to pending
    const { error } = await supabase
      .from(TABLES.CONNECTION_REQUESTS)
      .update({ status: 'pending', message: message ?? null, updated_at: new Date().toISOString() })
      .eq('id', existing.id);
    if (error) throw error;
    return existing.id;
  }

  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from(TABLES.CONNECTION_REQUESTS)
    .insert({
      task_id: taskId,
      tasker_id: taskerId,
      task_owner_id: taskOwnerId,
      status: 'pending',
      message: message ?? null,
      created_at: now,
      updated_at: now,
    })
    .select('id')
    .single();

  if (error) throw error;
  return (data as { id: string }).id;
}

// ============================================================================
// GET
// ============================================================================

export async function getConnectionRequests(taskId: string): Promise<ConnectionRequest[]> {
  const { data, error } = await supabase
    .from(TABLES.CONNECTION_REQUESTS)
    .select('*')
    .eq('task_id', taskId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return fromDbList<ConnectionRequest>(data ?? []);
}

export async function getConnectionRequestsForOwner(taskOwnerId: string): Promise<ConnectionRequest[]> {
  const { data, error } = await supabase
    .from(TABLES.CONNECTION_REQUESTS)
    .select('*')
    .eq('task_owner_id', taskOwnerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return fromDbList<ConnectionRequest>(data ?? []);
}

export async function getConnectionRequestsForTasker(taskerId: string): Promise<ConnectionRequest[]> {
  const { data, error } = await supabase
    .from(TABLES.CONNECTION_REQUESTS)
    .select('*')
    .eq('tasker_id', taskerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return fromDbList<ConnectionRequest>(data ?? []);
}

export async function getConnectionRequest(requestId: string): Promise<ConnectionRequest | null> {
  return getDocument<ConnectionRequest>(TABLES.CONNECTION_REQUESTS, requestId);
}

export async function getAcceptedConnectionsForUser(userId: string): Promise<ConnectionRequest[]> {
  const [asTasker, asOwner] = await Promise.all([
    supabase
      .from(TABLES.CONNECTION_REQUESTS)
      .select('*')
      .eq('tasker_id', userId)
      .eq('status', 'accepted'),
    supabase
      .from(TABLES.CONNECTION_REQUESTS)
      .select('*')
      .eq('task_owner_id', userId)
      .eq('status', 'accepted'),
  ]);

  if (asTasker.error) throw asTasker.error;
  if (asOwner.error) throw asOwner.error;

  const all = [...fromDbList<ConnectionRequest>(asTasker.data ?? []), ...fromDbList<ConnectionRequest>(asOwner.data ?? [])];
  const unique = Array.from(new Map(all.map((item) => [item.id, item])).values());
  return unique.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

export async function checkConnectionRequest(
  taskId: string,
  taskerId: string
): Promise<ConnectionRequest | null> {
  const { data, error } = await supabase
    .from(TABLES.CONNECTION_REQUESTS)
    .select('*')
    .eq('task_id', taskId)
    .eq('tasker_id', taskerId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return fromDb<ConnectionRequest>(data);
}

// ============================================================================
// HELPERS
// ============================================================================

export async function getTaskTitleForConnection(taskId: string): Promise<string> {
  const fromHatch = await getDocument<{ title?: string }>(TABLES.TASK_HATCH_SUBMISSIONS, taskId);
  if (fromHatch?.title) return fromHatch.title;

  const fromTasks = await getDocument<{ title?: string }>(TABLES.TASKS, taskId);
  if (fromTasks?.title) return fromTasks.title;

  return 'Task';
}

export async function getDisplayNameForUser(userId: string, fallback: string): Promise<string> {
  try {
    const { data } = await supabase
      .from(TABLES.USERS)
      .select('display_name, email')
      .eq('id', userId)
      .maybeSingle();
    if (data?.display_name) return data.display_name;
    if (data?.email) return (data.email as string).split('@')[0];
  } catch { /* ignore */ }

  try {
    const { data } = await supabase
      .from(TABLES.TASKER_PROFILES)
      .select('personal_info')
      .eq('id', userId)
      .maybeSingle();
    if (data?.personal_info) {
      const pi = data.personal_info as { displayName?: string; firstName?: string; lastName?: string };
      if (pi.displayName) return pi.displayName;
      if (pi.firstName && pi.lastName) return `${pi.firstName} ${pi.lastName}`;
    }
  } catch { /* ignore */ }

  return fallback;
}

// ============================================================================
// UPDATE
// ============================================================================

export async function acceptConnectionRequest(requestId: string): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from(TABLES.CONNECTION_REQUESTS)
    .update({ status: 'accepted', updated_at: now, responded_at: now })
    .eq('id', requestId);
  if (error) throw error;

  const request = await getConnectionRequest(requestId);
  if (!request) return;

  const { getOrCreateChat } = await import('./chat');
  const taskTitle = await getTaskTitleForConnection(request.taskId);
  await getOrCreateChat(requestId, request.taskId, taskTitle, [request.taskOwnerId, request.taskerId]);

  const ownerName = await getDisplayNameForUser(request.taskOwnerId, 'Task Owner');
  const taskerName = await getDisplayNameForUser(request.taskerId, 'Tasker');

  // Add to task_participants
  try {
    const { error: tpErr } = await supabase.from(TABLES.TASK_PARTICIPANTS).upsert({
      task_id: request.taskId,
      user_id: request.taskerId,
      role: 'collaborator',
      added_at: now,
    });
    if (tpErr) console.error('task_participants upsert:', tpErr);
  } catch (e) { console.error(e); }

  // Update task assigned_users
  try {
    for (const table of [TABLES.TASKS, TABLES.TASK_HATCH_SUBMISSIONS]) {
      const { data: row } = await supabase.from(table).select('assigned_users').eq('id', request.taskId).maybeSingle();
      if (row) {
        const arr: string[] = (row.assigned_users as string[]) ?? [];
        if (!arr.includes(request.taskerId)) {
          await supabase.from(table).update({ assigned_users: [...arr, request.taskerId] }).eq('id', request.taskId);
        }
      }
    }
  } catch (e) { console.error('assigned_users update:', e); }

  await notifyConnectionAccepted(request.taskerId, request.taskOwnerId, request.taskId, taskTitle, ownerName, requestId).catch(console.error);
  await notifyOwnerConnectionAccepted(request.taskOwnerId, request.taskerId, request.taskId, taskTitle, taskerName, requestId).catch(console.error);
}

// ============================================================================
// REALTIME SUBSCRIPTIONS
// ============================================================================

/** Subscribe to all connection requests involving a user (as tasker or owner). */
export function subscribeToUserConnections(
  userId: string,
  callback: (connections: ConnectionRequest[]) => void
): () => void {
  const load = async () => {
    const [{ data: asTasker }, { data: asOwner }] = await Promise.all([
      supabase.from(TABLES.CONNECTION_REQUESTS).select('*').eq('tasker_id', userId),
      supabase.from(TABLES.CONNECTION_REQUESTS).select('*').eq('task_owner_id', userId),
    ]);
    const all: ConnectionRequest[] = fromDbList<ConnectionRequest>([
      ...((asTasker as Record<string, unknown>[]) ?? []),
      ...((asOwner as Record<string, unknown>[]) ?? []),
    ]);
    const unique = Array.from(new Map(all.map((r) => [r.id, r])).values());
    callback(unique);
  };

  void load();

  const channel = supabase
    .channel(`connections-${userId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.CONNECTION_REQUESTS }, () => void load())
    .subscribe();

  return () => { void supabase.removeChannel(channel); };
}

export async function rejectConnectionRequest(requestId: string): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from(TABLES.CONNECTION_REQUESTS)
    .update({ status: 'rejected', updated_at: now, responded_at: now })
    .eq('id', requestId);
  if (error) throw error;

  const request = await getConnectionRequest(requestId);
  if (!request) return;

  const taskTitle = await getTaskTitleForConnection(request.taskId);
  const ownerName = await getDisplayNameForUser(request.taskOwnerId, 'Task Owner');
  await notifyConnectionRejected(request.taskerId, request.taskOwnerId, request.taskId, taskTitle, ownerName).catch(console.error);
}
